import {
  getDownloadCapabilityStatus,
  getEnhancedDirectoryDownloadCapabilityStatus,
} from "./downloadCapabilities";

function getBrowserWindow() {
  return typeof window !== "undefined" ? window : undefined;
}

function getPathSegments(safeRelativePath) {
  return String(safeRelativePath || "")
    .split("/")
    .filter(Boolean);
}

const RESERVED_DEVICE_NAME_PATTERN =
  /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i;

function normalizeFilesystemNameSegment(segment) {
  return String(segment == null ? "" : segment)
    .replace(/[\/\\:<>"|?*]/g, "_")
    .replace(/[\x00-\x1f\x7f]/g, "_")
    .trim()
    .replace(/[. ]+$/g, "");
}

export function sanitizeFilesystemNameSegment(segment, fallbackName) {
  let safeSegment = normalizeFilesystemNameSegment(segment);

  if (!safeSegment || safeSegment === "." || safeSegment === "..") {
    safeSegment = normalizeFilesystemNameSegment(fallbackName);
  }

  if (!safeSegment || safeSegment === "." || safeSegment === "..") {
    safeSegment = "download";
  }

  if (RESERVED_DEVICE_NAME_PATTERN.test(safeSegment)) {
    return safeSegment.replace(/^([^.]+)/, "$1_");
  }

  return safeSegment;
}

function isDirectoryNotFoundError(error) {
  return Boolean(
    error &&
      (error.name === "NotFoundError" ||
        /does not exist/i.test(error.message || ""))
  );
}

function getNumberedFileName(fileName, suffix) {
  if (suffix === 0) return fileName;

  const lastDotIndex = fileName.lastIndexOf(".");
  const hasExtension = lastDotIndex > 0;
  const baseName = hasExtension ? fileName.slice(0, lastDotIndex) : fileName;
  const extension = hasExtension ? fileName.slice(lastDotIndex) : "";

  return `${baseName} (${suffix})${extension}`;
}

async function directoryExists({ rootDirectoryHandle, directoryName }) {
  try {
    await rootDirectoryHandle.getDirectoryHandle(directoryName, {
      create: false,
    });
    return true;
  } catch (error) {
    if (isDirectoryNotFoundError(error)) {
      return false;
    }

    throw error;
  }
}

export async function createUniqueDirectoryHandle({
  rootDirectoryHandle,
  preferredName,
}) {
  if (!rootDirectoryHandle) {
    throw new Error("A root directory handle is required.");
  }

  const baseDirectoryName = sanitizeFilesystemNameSegment(
    preferredName,
    "Folder"
  );
  let suffix = 0;

  while (true) {
    const directoryName =
      suffix === 0 ? baseDirectoryName : `${baseDirectoryName} (${suffix})`;
    const exists = await directoryExists({
      rootDirectoryHandle,
      directoryName,
    });

    if (!exists) {
      const directoryHandle = await rootDirectoryHandle.getDirectoryHandle(
        directoryName,
        { create: true }
      );

      return {
        directoryHandle,
        directoryName,
      };
    }

    suffix += 1;
  }
}

export async function createUniqueFileHandle({
  rootDirectoryHandle,
  preferredName,
}) {
  if (!rootDirectoryHandle) {
    throw new Error("A root directory handle is required.");
  }

  const baseFileName = sanitizeFilesystemNameSegment(
    preferredName,
    "download-file"
  );
  let suffix = 0;

  while (true) {
    const fileName = getNumberedFileName(baseFileName, suffix);

    try {
      await rootDirectoryHandle.getFileHandle(fileName, { create: false });
      suffix += 1;
    } catch (error) {
      if (!error || error.name !== "NotFoundError") throw error;

      const fileHandle = await rootDirectoryHandle.getFileHandle(fileName, {
        create: true,
      });
      return { fileHandle, fileName };
    }
  }
}

export const FOLDER_DOWNLOAD_UNSUPPORTED_MESSAGE =
  "Folder downloads with preserved hierarchy and resume require a supported desktop browser such as Chrome or Edge. Individual files can still be downloaded.";

export function isFilesystemDownloadSupported() {
  return getDownloadCapabilityStatus(getBrowserWindow());
}

export function getFolderDownloadCapabilityStatus() {
  const status = getEnhancedDirectoryDownloadCapabilityStatus(
    getBrowserWindow()
  );

  return {
    ...status,
    reason: status.supported ? "" : FOLDER_DOWNLOAD_UNSUPPORTED_MESSAGE,
  };
}

export function selectDownloadDirectory() {
  const browserWindow = getBrowserWindow();

  if (
    !browserWindow ||
    typeof browserWindow.showDirectoryPicker !== "function"
  ) {
    throw new Error("File System Access directory picker is not available.");
  }

  return browserWindow.showDirectoryPicker();
}

export async function ensureDirectoryPath({
  rootDirectoryHandle,
  pathSegments,
}) {
  if (!rootDirectoryHandle) {
    throw new Error("A root directory handle is required.");
  }

  let currentDirectoryHandle = rootDirectoryHandle;

  for (const segment of pathSegments || []) {
    if (!segment) {
      continue;
    }

    const safeSegment = sanitizeFilesystemNameSegment(segment, "Folder");

    currentDirectoryHandle = await currentDirectoryHandle.getDirectoryHandle(
      safeSegment,
      { create: true }
    );
  }

  return currentDirectoryHandle;
}

export async function ensureFileHandle({
  rootDirectoryHandle,
  safeRelativePath,
}) {
  const pathSegments = getPathSegments(safeRelativePath);
  const rawFileName = pathSegments[pathSegments.length - 1];

  if (!rawFileName) {
    throw new Error("A safe relative file path is required.");
  }

  const directoryHandle = await ensureDirectoryPath({
    rootDirectoryHandle,
    pathSegments: pathSegments.slice(0, -1),
  });
  const fileName = sanitizeFilesystemNameSegment(rawFileName, "download-file");
  const fileHandle = await directoryHandle.getFileHandle(fileName, {
    create: true,
  });

  return {
    directoryHandle,
    fileHandle,
  };
}

export async function verifyPositionedWrite({ fileHandle }) {
  if (!fileHandle) {
    throw new Error("A file handle is required.");
  }

  const writable = await fileHandle.createWritable();

  await writable.write({
    type: "write",
    position: 0,
    data: "ABC",
  });
  await writable.write({
    type: "write",
    position: 3,
    data: "XYZ",
  });
  await writable.close();

  const file = await fileHandle.getFile();
  const contents = await file.text();

  return {
    success: contents === "ABCXYZ",
    contents,
  };
}
